import { RefObject, useEffect, useRef } from "react"
import FullCalendar from "@fullcalendar/react"
import { DateTime } from "luxon"
import { SLOT_SIZE } from "./helpers"

type Options = {
    thresholdPx?: number
    debounceMs?: number
    cooldownMs?: number
    hourForNonToday?: number // default 8
}

export function useAutoEdgeTimelineNavigation(
    calendarRef: RefObject<FullCalendar | null>,
    containerRef: RefObject<HTMLElement | null>,
    isEnabled: boolean,
    opts?: Options,
) {
    const scrollerRef = useRef<HTMLElement | null>(null)
    const debounceTimerRef = useRef<number | null>(null)

    const coolingRef = useRef(false)
    const armedRef = useRef(false)
    const coolingTimerRef = useRef<number | null>(null)
    const scrollRafRef = useRef<number | null>(null)
    const attachTimerRef = useRef<number | null>(null)
    const observerRef = useRef<MutationObserver | null>(null)

    useEffect(() => {
        if (!isEnabled || !calendarRef.current || !containerRef.current) return

        const threshold = opts?.thresholdPx ?? 2 * SLOT_SIZE // Threshold at the edge: ~2 grid cells → less sensitive to small tremors, tied to UI size
        const debounceMs = opts?.debounceMs ?? 60 // debounce to filter out micro-scroll movements without noticeable delay
        const cooldownMs = opts?.cooldownMs ?? 450 // Cooldown after auto-refresh: allow FullCalendar to redraw and remove scroll inertia
        const nonTodayH = Number.isFinite(opts?.hourForNonToday) ? (opts!.hourForNonToday as number) : 8

        const api = calendarRef.current.getApi()
        const rootEl = containerRef.current

        const clearCoolingTimer = () => {
            if (coolingTimerRef.current) {
                window.clearTimeout(coolingTimerRef.current)
                coolingTimerRef.current = null
            }
        }
        const setCooling = () => {
            coolingRef.current = true
            clearCoolingTimer()
            coolingTimerRef.current = window.setTimeout(() => {
                coolingRef.current = false
                coolingTimerRef.current = null
            }, cooldownMs)
        }

        const getCenterHour = (anchorDay: DateTime, nonTodayHour: number) => {
            const now = DateTime.now()
            return anchorDay.hasSame(now, "day") ? now.set({ minute: 0, second: 0, millisecond: 0 }).hour : nonTodayHour
        }

        const cancelScrollRaf = () => {
            if (scrollRafRef.current !== null) {
                window.cancelAnimationFrame(scrollRafRef.current)
                scrollRafRef.current = null
            }
        }

        const navigateBy = (dir: 1 | -1) => {
            const stepDays = 2 * dir
            const viewStart = DateTime.fromJSDate(api.view.currentStart)
            const newStart = viewStart.plus({ days: stepDays })
            const middleDay = newStart.plus({ days: 1 })

            api.gotoDate(newStart.toJSDate())

            const hours = getCenterHour(middleDay, nonTodayH)
            const dur = { days: 1, hours } as const

            cancelScrollRaf()
            scrollRafRef.current = window.requestAnimationFrame(() => {
                api.scrollToTime(dur as any)
                scrollRafRef.current = null
            })

            setCooling()
            armedRef.current = false
        }

        const onScroll = () => {
            if (!scrollerRef.current || coolingRef.current) return
            const el = scrollerRef.current
            ;(() => {
                const prev = (onScroll as any)._prevLeft ?? el.scrollLeft
                const delta = el.scrollLeft - prev
                ;(onScroll as any)._dir = delta === 0 ? 0 : delta > 0 ? 1 : -1
                ;(onScroll as any)._prevLeft = el.scrollLeft
            })()

            if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = window.setTimeout(() => {
                const el = scrollerRef.current!
                const { scrollLeft, clientWidth, scrollWidth } = el
                const atStart = scrollLeft <= threshold
                const atEnd = scrollLeft + clientWidth >= scrollWidth - threshold

                const dir = (onScroll as any)._dir as -1 | 0 | 1
                if (dir === 1 && atEnd) {
                    navigateBy(1)
                    return
                }
                if (dir === -1 && atStart) {
                    navigateBy(-1)
                    return
                }
            }, debounceMs)
        }

        const detach = () => {
            if (scrollerRef.current) {
                scrollerRef.current.removeEventListener("scroll", onScroll)
            }
            if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)

            scrollerRef.current = null
        }

        const pickTimelineScroller = () => {
            const candidates = Array.from(rootEl.querySelectorAll<HTMLElement>(".fc-scroller"))
            if (!candidates.length) return null
            let best: HTMLElement | null = null
            let bestWidth = 0
            for (const el of candidates) {
                const sw = el.scrollWidth
                const cw = el.clientWidth
                if (sw > cw + 8 && sw > bestWidth) {
                    best = el
                    bestWidth = sw
                }
            }
            return best
        }

        const attach = () => {
            detach()
            const found = pickTimelineScroller()
            if (!found) return
            scrollerRef.current = found
            scrollerRef.current.addEventListener("scroll", onScroll, { passive: true })

            armedRef.current = false
            setCooling()
        }

        attach()
        if (!scrollerRef.current) {
            attachTimerRef.current = window.setTimeout(() => {
                attach()
                attachTimerRef.current = null
            }, 0)

            return () => {
                if (attachTimerRef.current) {
                    window.clearTimeout(attachTimerRef.current)
                    attachTimerRef.current = null
                }
                detach()
                if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)
                observerRef.current?.disconnect()
                clearCoolingTimer()
                cancelScrollRaf()
            }
        }

        observerRef.current = new MutationObserver(() => {
            if (!scrollerRef.current || !rootEl.contains(scrollerRef.current)) {
                attach()
            }
        })
        observerRef.current.observe(rootEl, { childList: true, subtree: true })

        return () => {
            detach()
            if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)
            observerRef.current?.disconnect()
            if (attachTimerRef.current) {
                window.clearTimeout(attachTimerRef.current)
                attachTimerRef.current = null
            }
            clearCoolingTimer()
            cancelScrollRaf()
        }
    }, [
        calendarRef,
        containerRef,
        isEnabled,
        opts?.thresholdPx,
        opts?.debounceMs,
        opts?.cooldownMs,
        opts?.hourForNonToday,
    ])
}
